import { useState } from 'react';
import { Pause } from 'lucide-react';
import clsx from 'clsx';
import { DealCard } from '../DealCard';
import { useT } from '../../i18n';
import { formatMoney } from '../../lib/format';
import { visibleInquiryAmount, type Inquiry } from '../../types/api';
import styles from './StageColumn.module.css';

export type StageColumnTone =
  | 'validation'
  | 'verification'
  | 'prioritization'
  | 'realization'
  | 'integrator_selection'
  | 'tkp'
  | 'won'
  | 'lost'
  // Issue #52 — activity («По делам») buckets reuse the stage colour tokens
  | 'act_overdue'
  | 'act_today'
  | 'act_this_week'
  | 'act_next_week'
  | 'act_no_date'
  | 'act_later';

export interface StageColumnProps {
  tone: StageColumnTone;
  title: string;
  /** Active (non-paused) deals shown as cards. */
  items: Inquiry[];
  /** Paused deals — rendered inside the collapsed "⏸ n" block. */
  pausedItems?: Inquiry[];
  onOpen: (inquiry: Inquiry) => void;
  /** While a card is being dragged: is this column an allowed target?
   *  `undefined` = no drag in progress (drop handlers disabled). */
  dropState?: 'valid' | 'invalid';
  /** Fired with the dragged inquiry id when a card is dropped here. */
  onDropCard?: (inquiryId: string) => void;
  /** Drag wiring for the cards in this column. */
  cardDraggable?: boolean;
  draggingId?: string | null;
  onCardDragStart?: (inquiry: Inquiry, e: React.DragEvent<HTMLElement>) => void;
  onCardDragEnd?: (e: React.DragEvent<HTMLElement>) => void;
  /** «Взять в работу» wiring (issue #23) — manufacturer on unassigned deals. */
  canTake?: boolean;
  onTake?: (inquiry: Inquiry) => void;
  /** Issue #67 — the «Отбой» (lost) column renders its cards drained/grey so
   *  they read as inactive. Behaviour (open/drag/take) is unchanged. */
  muted?: boolean;
}

/**
 * Kanban column: coloured stage header (dot + name + count, sum in mono
 * below), deal cards, and a collapsed paused block. Terminal columns
 * (won/lost) use the `quiet` style — dashed, muted cards.
 */
export function StageColumn({
  tone,
  title,
  items,
  pausedItems = [],
  onOpen,
  dropState,
  onDropCard,
  cardDraggable,
  draggingId,
  onCardDragStart,
  onCardDragEnd,
  canTake,
  onTake,
  muted,
}: StageColumnProps) {
  const { t, locale } = useT();
  const [pausedOpen, setPausedOpen] = useState(false);

  // Issue #50 — only deals at stages that may show an amount contribute;
  // early-stage columns (and any column without amounts) render no «0 ₽».
  const sum = items.reduce((acc, inq) => acc + (visibleInquiryAmount(inq) ?? 0), 0);
  const quiet = tone === 'won' || tone === 'lost';
  const totalCount = items.length + pausedItems.length;

  const handleDragOver = (e: React.DragEvent<HTMLElement>) => {
    if (!dropState) return;
    // preventDefault on both valid and invalid targets so the drop event
    // fires everywhere — the page resolves and explains invalid drops.
    e.preventDefault();
    e.dataTransfer.dropEffect = dropState === 'valid' ? 'move' : 'none';
  };

  const handleDrop = (e: React.DragEvent<HTMLElement>) => {
    if (!dropState) return;
    e.preventDefault();
    const id = e.dataTransfer.getData('text/plain');
    if (id) onDropCard?.(id);
  };

  return (
    <section
      className={clsx(
        styles.col,
        styles[`tone_${tone}`],
        quiet && styles.quiet,
        dropState === 'valid' && styles.dropValid,
        dropState === 'invalid' && styles.dropInvalid,
      )}
      aria-label={title}
      aria-dropeffect={dropState ? (dropState === 'valid' ? 'move' : 'none') : undefined}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
    >
      <header className={styles.head}>
        <div className={styles.row}>
          <span className={styles.dot} aria-hidden="true" />
          <span className={styles.name} title={title}>
            {title}
          </span>
          <span className={styles.count}>{totalCount}</span>
        </div>
        {/* sum row is always rendered to keep header heights aligned (#45);
            issue #50 supersedes #48: early stages and amount-less columns show
            nothing instead of a «0 ₽» placeholder. */}
        <span className={styles.sum}>{sum > 0 ? formatMoney(sum, locale) : ''}</span>
      </header>

      <div className={styles.body}>
        {items.length === 0 && pausedItems.length === 0 ? (
          <div className={styles.empty}>{t('pipeline.emptyStage')}</div>
        ) : (
          items.map((inq) => (
            <DealCard
              key={inq.id}
              inquiry={inq}
              onOpen={onOpen}
              muted={muted}
              draggable={cardDraggable}
              dragging={draggingId === inq.id}
              onDragStart={onCardDragStart ? (e) => onCardDragStart(inq, e) : undefined}
              onDragEnd={onCardDragEnd}
              canTake={canTake}
              onTake={onTake}
            />
          ))
        )}

        {pausedItems.length > 0 && (
          <div className={styles.pausedBlock}>
            <button
              type="button"
              className={styles.pausedToggle}
              aria-expanded={pausedOpen}
              aria-label={t('inquiries.pausedListLabel')}
              onClick={() => setPausedOpen((v) => !v)}
            >
              <Pause size={12} aria-hidden="true" />
              {t('inquiries.pausedCount', { count: pausedItems.length })}
            </button>
            {pausedOpen && (
              <div className={styles.pausedList}>
                {pausedItems.map((inq) => (
                  <DealCard key={inq.id} inquiry={inq} onOpen={onOpen} dimmed />
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </section>
  );
}
